import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useGroupSearch } from '../../../../api/groups'
import { getSuppression, setSuppression } from '../../../../utils/globalSuppression'
import { getInitials } from './getInitials'
import { ScopeDisplayItem } from './ScopeDisplayItem'
import ScopeSelect from './ScopeSelect'

/** Search for all the options */
const useGroupTypeOptions = (scope, includeAdvisors, myAdvisor, labels) => {
  const scopeQuery = useMemo(() => ({
    filters: {
      levelTypeId: [{
        value: scope ? scope.groupTypeId : 301,
        op: 'eq'
      }]
    },
    sort: [{ field: 'longName', dir: 'asc' }],
    take: 1000
  }), [scope])

  const { data, isLoading } = useGroupSearch(scopeQuery, { enabled: includeAdvisors })
  const { options, pinnedOptions } = useMemo(() => {
    const pinnedResult = []
    if (myAdvisor) {
      pinnedResult.push({
        label: labels?.allMyItems ?? 'My Clients',
        value: JSON.stringify({ type: 301, id: myAdvisor.groupId }),
        attributes: {
          type: 301,
          id: myAdvisor.groupId,
          avatarUrl: myAdvisor?.avatarUrl ?? myAdvisor.extras?.profileData?.profilePic,
          abbreviation: 'My',
          subTitle: labels.subTitle
        }
      })
    }

    pinnedResult.push({
      label: labels?.allItems ?? 'All Clients',
      value: JSON.stringify({ type: 1001, id: null }),
      attributes: { type: 1001, id: null, abbreviation: 'All', subTitle: labels.subTitle }
    })

    const result = []
    if (includeAdvisors) {
      const d = data?.data || []
      d.filter(x => x.levelTypeId !== 301 || (x.levelTypeId === 301 && x.groupId !== myAdvisor?.groupId)).forEach(g => {
        result.push({
          label: g.longName,
          value: JSON.stringify({ type: g.levelTypeId, id: g.groupId }),
          attributes: {
            type: g.levelTypeId,
            id: g.groupId,
            avatarUrl: g.avatarUrl ?? g.profile?.profileData?.profilePic,
            abbreviation: getInitials(g.longName),
            subTitle: labels.subTitle
          }
        })
      })
    }

    return {
      options: result,
      pinnedOptions: pinnedResult
    }
  }, [data, myAdvisor, includeAdvisors, labels])

  return { options, pinnedOptions, isLoading }
}

const useOnChangeGroupScope = (scope, setScope) => {
  const onChangeGroupScope = useCallback((group) => {
    const levelTypeId = group.attributes.type
    const groupId = group.attributes.id

    const scopeValues = levelTypeId === 1001 ? [] : [groupId]

    // setScope sets url state and such appropriately
    setScope({ values: scopeValues })
  }, [setScope])

  const [currentValue, currentValueString] = useMemo(() => {
    const type = scope?.values?.length ? scope.groupTypeId : 1001
    const scopeValue = { type, id: type === 1001 ? null : scope.values?.at(0) }
    return [scopeValue, JSON.stringify(scopeValue)]
  }, [scope])

  return { onChangeGroupScope, currentValue, currentValueString }
}

// Search for group matching the specified scope
const useDisplayGroup = (currentScope, labels) => {
  const scopeQuery = useMemo(() => {
    if (!currentScope?.id) return null
    return {
      filters: {
        levelTypeId: [{
          value: currentScope.type,
          op: 'eq'
        }],
        groupId: [{
          op: 'in',
          value: [currentScope.id]
        }]
      },
      take: 1
    }
  }, [currentScope?.type, currentScope?.id])

  const { data, isFetching } = useGroupSearch(scopeQuery, { enabled: !!scopeQuery })
  const group = useMemo(() => {
    if (currentScope?.type === 1001 && currentScope?.id === null) {
      return {
        label: labels?.allItems ?? 'All Clients',
        abbreviation: 'All'
      }
    }
    const g = data?.data ? data.data.at(0) : null
    return g ? ({
      label: g.longName,
      avatarUrl: g?.avatarUrl ?? g.extras?.profileData?.profilePic,
      abbreviation: g.abbreviation || getInitials(g.longName),
      subTitle: labels?.subTitle
    }) : null
  }, [data, currentScope, labels])

  return {
    group,
    isGroupFetching: isFetching
  }
}

const GroupScopeDisplay = ({
  scope,
  setScope,
  includeAdvisors,
  myAdvisor,
  isLoading,
  hideAvatars,
  size,
  disableReset,
  labels
}) => {
  const { onChangeGroupScope, currentValue } = useOnChangeGroupScope(scope, setScope)

  /* Here we need to take the raw value, and determine the system's representation of that level */
  const { group, isGroupFetching } = useDisplayGroup(currentValue, labels)

  /* If we've selected something we don't have, it is invalid - probably deleted - the provider stores selections in local storage maybe */
  const isValid = useMemo(() => {
    return !isGroupFetching && !!group
  }, [group, isGroupFetching])

  const { options, pinnedOptions, isFetching: optionsLoading } = useGroupTypeOptions(scope, includeAdvisors, myAdvisor, labels)

  /**
   * If we've selected an invalid selection, then try to reset ONE TIME
   */
  useEffect(() => {
    if (disableReset) return

    const key = 'GroupScopeDisplay'
    const suppression = getSuppression(key)
    if (!suppression && !isValid && !isLoading && !optionsLoading) {
      setSuppression(key, true)
      onChangeGroupScope(pinnedOptions.at(0))
    } else if (!suppression && !isLoading && !optionsLoading) {
      setSuppression(key, true)
    }
  }, [isValid, onChangeGroupScope, pinnedOptions, isLoading, optionsLoading, scope, disableReset])

  return (
    <ScopeSelect
      options={options}
      pinnedOptions={pinnedOptions}
      isLoading={isLoading || isGroupFetching}
      onSelect={onChangeGroupScope}
    >
      {!isValid ? (
        <ScopeDisplayItem
          name='Select an Option'
          hideAvatar={hideAvatars}
          size={size}
        />
      ) : group ? (
        <ScopeDisplayItem
          name={group.label}
          subTitle={group.subTitle}
          avatarSrc={group?.avatarUrl}
          abbreviation={group?.abbreviation}
          hideAvatar={hideAvatars}
          size={size}
        />
      ) : null}
    </ScopeSelect>
  )
}

GroupScopeDisplay.propTypes = {
  scope: PropTypes.object,
  setScope: PropTypes.func,
  includeAdvisors: PropTypes.bool,
  myAdvisor: PropTypes.shape({
    groupId: PropTypes.string,
    shortName: PropTypes.string,
    longName: PropTypes.string
  }),
  isLoading: PropTypes.bool,
  hideAvatars: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'lg']),
  disableReset: PropTypes.bool,
  labels: PropTypes.shape({
    subTitle: PropTypes.string,
    allItems: PropTypes.string,
    allMyItems: PropTypes.string
  })
}

GroupScopeDisplay.defaultProps = {
  labels: {
    subTitle: '',
    allItems: 'All Clients',
    allMyItems: 'My Clients'
  }
}

export default GroupScopeDisplay
